import { useCallback, useEffect, useState } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isLinkNode } from '@lexical/link';
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
} from 'lexical';
import { $setBlocksType } from '@lexical/selection';
import { TableNode } from '@lexical/table';

import { Box } from '../../../../Box';
import { Icon } from '../../../../Icon';
import { DropdownButton } from '../../../../DropdownButton';
import { ToolbarButton } from '../common';
import { getSelectedNode } from '../../utils';
import {
  $createCodeBlockNode,
  $createReferenceNode,
  $createMentionNode,
} from '../../nodes';
import { useDialog } from '../../../../hooks';

import { LinkDialog, QuoteDialog, TableDialog } from './components';
import { useSymbols } from './hooks';

interface LinkAndTablePluginProps {
  link: boolean;
  table: boolean;
  reference: boolean;
  codeBlock: boolean;
  quote: boolean;
  symbols: boolean;
  mention: boolean;
}

export const LinkAndTablePlugin = ({
  link,
  table,
  reference,
  codeBlock,
  quote,
  symbols: symbolsNode,
  mention,
}: LinkAndTablePluginProps) => {
  const [editor] = useLexicalComposerContext();
  const { onOpen, dialogsState, onClose } = useDialog({
    link: false,
    table: false,
    quote: false,
  });
  const [activeParams, setActiveParams] = useState({
    table: false,
    link: false,
    reference: false,
    code: false,
  });
  const [selectionText, setSelectionText] = useState('');
  const [selectionUrl, setSelectionUrl] = useState('');

  const symbols = useSymbols(editor);

  const updateToolbar = useCallback(() => {
    editor.read(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        const textContent = selection.getTextContent();
        const node = getSelectedNode(selection);
        const parent = node.getParent();

        let url = '';
        if (textContent) {
          if ($isLinkNode(node)) {
            url = node.exportJSON().url;
          }

          if ($isLinkNode(parent)) {
            url = parent.exportJSON().url;
          }

          if (url) {
            setSelectionUrl(url);
          }

          setSelectionText(textContent);
        } else {
          setSelectionText('');
          setSelectionUrl('');
        }

        const tableNode = $getNearestNodeOfType(node, TableNode);

        if (tableNode !== null) {
          setActiveParams((prev) => ({ ...prev, table: true }));
        } else {
          setActiveParams((prev) => ({ ...prev, table: false }));
        }

        if (node.getType() === 'link' || parent?.getType() === 'link') {
          setActiveParams((prev) => ({ ...prev, link: true }));
        } else {
          setActiveParams((prev) => ({ ...prev, link: false }));
        }

        if (
          node.getType() === 'reference-node' ||
          parent?.getType() === 'reference-node'
        ) {
          setActiveParams((prev) => ({ ...prev, reference: true }));
        } else {
          setActiveParams((prev) => ({ ...prev, reference: false }));
        }

        if (
          node.getType() === 'code-block-node' ||
          parent?.getType() === 'code-block-node'
        ) {
          setActiveParams((prev) => ({ ...prev, code: true }));
        } else {
          setActiveParams((prev) => ({ ...prev, code: false }));
        }
      }
    });
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
    );
  }, [editor, updateToolbar]);

  const handleLinkDialogClose = () => {
    onClose('link')();
    setSelectionText('');
    setSelectionUrl('');
  };

  const handleReferenceClick = () => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        const node = getSelectedNode(selection);
        const parent = node.getParent();

        if (
          node.getType() === 'reference-node' ||
          parent?.getType() === 'reference-node'
        ) {
          $setBlocksType(selection, () => $createParagraphNode());

          return;
        }

        if (node.getType() === 'root') {
          $insertNodes([$createReferenceNode()]);

          return;
        }

        $setBlocksType(selection, () => $createReferenceNode());
      }
    });
  };

  const handleBlockCodeClick = () => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        const node = getSelectedNode(selection);
        const parent = node.getParent();

        if (
          node.getType() === 'code-block-node' ||
          parent?.getType() === 'code-block-node'
        ) {
          $setBlocksType(selection, () => $createParagraphNode());

          return;
        }

        if (node.getType() === 'root') {
          $insertNodes([$createCodeBlockNode()]);

          return;
        }

        $setBlocksType(selection, () => $createCodeBlockNode());
      }
    });
  };

  const handleQuoteDialogClose = () => {
    onClose('quote')();
    setSelectionText('');
  };

  const handleMentionClick = () => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        const mentionNode = $createMentionNode();

        $insertNodes([mentionNode]);
      }
    });
  };

  return (
    <Box display="flex" alignItems="center" gap="2">
      {link && (
        <ToolbarButton onClick={onOpen('link')} tooltip="Вставить ссылку">
          <Icon
            name="chain_outline"
            size="16"
            color={
              activeParams.link
                ? 'original.brand.primary'
                : 'text.light.primary'
            }
          />
        </ToolbarButton>
      )}

      {table && (
        <ToolbarButton onClick={onOpen('table')} tooltip="Вставить таблицу">
          <Icon
            name="table_header_outline"
            size="16"
            color={
              activeParams.table
                ? 'original.brand.primary'
                : 'text.light.primary'
            }
          />
        </ToolbarButton>
      )}

      {reference && (
        <ToolbarButton
          onClick={handleReferenceClick}
          tooltip="Вставить блок справки"
        >
          <Icon
            name="vkhrtek_reference"
            color={
              activeParams.reference
                ? 'original.brand.primary'
                : 'text.light.primary'
            }
            size="16"
          />
        </ToolbarButton>
      )}

      {codeBlock && (
        <ToolbarButton
          onClick={handleBlockCodeClick}
          tooltip="Вставить блок кода"
        >
          <Icon
            name="vkhrtek_code_block"
            size="16"
            color={
              activeParams.code
                ? 'original.brand.primary'
                : 'text.light.primary'
            }
          />
        </ToolbarButton>
      )}

      {quote && (
        <ToolbarButton onClick={onOpen('quote')} tooltip="Вставить цитату">
          <Icon name="quote_closing" size="16" color="text.light.primary" />
        </ToolbarButton>
      )}

      {symbolsNode && (
        <DropdownButton
          menuItems={symbols}
          variant="tertiaryLight"
          verticalPosition="bottom"
          horizontalPosition="left"
          button={({ onClick }) => (
            <ToolbarButton onClick={onClick} tooltip="Вставить спецсимвол">
              <Icon name="vkhrtek_omega" size="16" color="text.light.primary" />
            </ToolbarButton>
          )}
        />
      )}

      {mention && (
        <ToolbarButton
          onClick={handleMentionClick}
          tooltip="Упоминание пользователя"
        >
          <Icon name="vkhrtek_at" size="16" color="text.light.primary" />
        </ToolbarButton>
      )}

      {dialogsState.link && (
        <LinkDialog
          editor={editor}
          open={dialogsState.link}
          onClose={handleLinkDialogClose}
          selectionText={selectionText}
          selectionUrl={selectionUrl}
        />
      )}

      {dialogsState.table && (
        <TableDialog
          editor={editor}
          open={dialogsState.table}
          onClose={onClose('table')}
        />
      )}

      {dialogsState.quote && (
        <QuoteDialog
          editor={editor}
          open={dialogsState.quote}
          onClose={handleQuoteDialogClose}
          selectionText={selectionText}
        />
      )}
    </Box>
  );
};
